import { OpenGraphType, Paywall, MetaProps, SpeakableSpecification } from './entities';

function isSpeakableSpecification(value: unknown): value is SpeakableSpecification {
  if (typeof value !== 'object' || value === null || !('@type' in value)) {
    return false;
  }
  const typeField = Reflect.get(value, '@type');
  return typeField === 'SpeakableSpecification';
}

export function parseSpeakableValue(speakable: string | SpeakableSpecification): SpeakableSpecification | undefined {
  if (typeof speakable !== 'string') {
    return isSpeakableSpecification(speakable) ? speakable : undefined;
  }

  try {
    const parsed: unknown = JSON.parse(speakable);
    return isSpeakableSpecification(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export type SeoProps = MetaProps & { paywall?: Paywall };

export const defaultImage =
  'https://cdn.benzinga.com/files/imagecache/bz2_opengraph_meta_image_400x300/sites/all/themes/bz2/images/bz-icon.png';

export const getOgImageType = (ogImage: unknown): 'image/png' | 'image/jpeg' =>
  String(ogImage).toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';

export const extractSeoMeta = async (props: SeoProps = {} as SeoProps) => {
  let { ogDescription, ogImage, ogTitle, ogType, title, twitterDescription, twitterImage, twitterTitle } = props;

  title = title?.substring(0, 105) || 'Benzinga - Actionable Trading Ideas, Real-Time News, Financial Insight';
  ogDescription = ogDescription ?? props.description;
  ogImage = ogImage || defaultImage;
  ogTitle = ogTitle ?? title;
  ogType = ogType ?? ('website' as OpenGraphType);
  twitterDescription = twitterDescription ?? props.description;
  twitterImage = twitterImage || props.image || ogImage;
  twitterTitle = twitterTitle ?? title;

  if (!title?.toLowerCase().includes('benzinga')) {
    title = `${title} - Benzinga`;
  }

  return {
    author: props.author,
    canonical: props.canonical,
    description: props.description,
    hrefLanguage: props.hrefLanguage,
    image: props.image,
    ogDescription,
    ogImage,
    ogImageHeight: props.ogImageHeight ?? (ogImage === defaultImage ? 300 : 630),
    ogImageWidth: props.ogImageWidth ?? (ogImage === defaultImage ? 400 : 1200),
    ogTitle,
    ogType,
    ogVideoUrl: props.ogVideoUrl,
    robots: props.robots,
    shortUrl: props.shortUrl,
    symbol: props.symbol,
    title,
    twitterCard: props.twitterCard,
    twitterDescription,
    twitterImage,
    twitterTitle,
  };
};

export type ExtractedSeoMeta = ReturnType<typeof extractSeoMeta>;

export const decodeDescription = (text: string) => {
  // return text ? decode(text) : '';
  return text ?? '';
};

export const escapeForScript = (json: string) => json.replace(/</g, '\\u003c').replace(/-->/g, '--\\u003e');

export const formatStringToValidJsonString = (text: string) => {
  return text
    .replace(/<[^>]*>?/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&#8216;/g, "'") // Opening single quote
    .replace(/&#8217;/g, "'") // Closing single quote
    .replace(/&#8220;/g, '"') // Opening double quote
    .replace(/&#8221;/g, '"') // Closing double quote
    .replace(/\n/g, ' ');
};
